import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  INBOX_DOCS_FILE_CATEGORY,
  INBOX_DOCS,
} from '../../../../constants';
import { formatBytes } from '../../../../services/utils';

import chboxUnselected from '../../../../assets/inboxDocsMultibrand/check-unchecked.svg';
import chboxSelected from '../../../../assets/inboxDocsMultibrand/check-checked.svg';

import blackFolderIcon from '../../../../assets/inboxDocs/black-folder.svg';
import plusIcon from '../../../../assets/plus.svg';

import TouchableDiv from '../../../common/TouchableDiv';
import { PetItemCardImage } from '../../../myPets/PetItemCardImage';
import { InboxAndDocsMenu } from '../../InboxAndDocsMenu';
import {
  RecentFileIcon,
} from '../../recentActivity/RecentFileIcon';

import { useMixpanel } from '../../../../hooks';
import { EV40 } from '../../../../services/mixpanel';
import { filePermissionsLoaded } from '../../../../actions';

import './InboxAndDocsItemRow.css';
import {
  DATE_FORMATS,
  formatDateByLanguage,
} from '../../../../services/language.utils';
import { isEducationMaterialFile } from '../../../../services/inboxAndDocs';

const rowId = 'inboxAndDocsItemRowId';

const InboxAndDocsItemRow = ({
  changeLocation = () => { },
  currentFolderId = 0,
  goToFolder = () => { },
  isSelected = false,
  itemRow = {},
  onAssignTagPetClick = () => { },
  onEditTagPetClick = () => { },
  onFileRowClick = () => { },
  onRowSelection = () => { },
}) => {
  const store = useSelector(({
    inboxAndDocs,
    policies,
  }) => ({ inboxAndDocs, policies }), shallowEqual);

  const { t } = useTranslation('inboxAndDocs');

  const isFolder = useMemo(() => itemRow.Type
    === INBOX_DOCS_FILE_CATEGORY.folder, [itemRow.Type]);
  const isTrashFolder = useMemo(() => String(currentFolderId)
    === INBOX_DOCS.trash, [currentFolderId]);
  const { sendEvent } = useMixpanel();
  const dispatch = useDispatch();

  const handleFileClick = (file) => () => {
    if (String(currentFolderId) !== INBOX_DOCS.trash) {
      sendEvent(EV40({ fileName: `${file.Name}${file.Metadata.Extension}` }));
      const downloadDirectly = isEducationMaterialFile(itemRow.Id);
      onFileRowClick({ data: file, downloadDirectly });
    }
  };

  const handleRowSelection = () => {
    onRowSelection(itemRow);
  };

  const formatDate = (date) => {
    if (!date) {
      return '';
    }

    return formatDateByLanguage({
      date,
      dateFormat: DATE_FORMATS.shortWithMonthName,
    });
  };

  const formatFileSize = (size) => {
    if (!size) {
      return '-';
    }

    return (formatBytes(size));
  };

  const handleFolderClick = () => {
    if (!isTrashFolder) {
      const filePermissions = itemRow.FilePermissions;
      dispatch(filePermissionsLoaded(filePermissions));

      goToFolder({
        id: itemRow.Id,
        name: itemRow.Name,
      });
    }
  };

  const handleAssignPetTagClick = () => {
    onAssignTagPetClick({ fileId: itemRow.Id });
  };

  const handleEditPetTagClick = () => {
    onEditTagPetClick({
      fileId: itemRow.Id,
      previousPetIdTagged: itemRow.PetsId || [],
    });
  };

  const renderIconAndName = () => {
    if (isFolder) {
      return (
        <div className="Docs-folder-icon-row-container">
          <img
            alt=""
            className="Docs-folder-icon-row"
            src={blackFolderIcon}
          />

          <TouchableDiv
            className="Docs-folder-icon-label-row"
            id={`itemRow${itemRow.Id}`}
            onClick={handleFolderClick}
          >
            <span>{itemRow.Name}</span>
          </TouchableDiv>
        </div>
      );
    }

    const { Metadata, Name } = itemRow;
    const fileExtension = Metadata.Extension?.toLowerCase();
    const boldStyle = ' Bold';

    return (
      <div className="Docs-file-row-flex">
        <RecentFileIcon
          alt="file extension icon"
          className="Docs-file-icon-row"
          fileExtension={fileExtension}
        />

        <div className="Docs-file-description-col Docs-file-just-cont-cent">
          <TouchableDiv
            className="Docs-file-description-touchable-name"
            id={`fileRowElement${itemRow.Id}`}
            onClick={handleFileClick(itemRow)}
          >
            <span className={`No-clicked${!Metadata.IsRead ? boldStyle : ''}`}>
              {Name}
            </span>
          </TouchableDiv>
        </div>
      </div>
    );
  };

  const AssignPetsButton = () => (
    <TouchableDiv
      className="Assign-pet-button-container"
      id={`assignPetId${itemRow.Id}`}
      onClick={handleAssignPetTagClick}
    >
      <span>{t('assign')}</span>
    </TouchableDiv>
  );

  const PetImage = ({
    className,
    petImage,
    petName,
    petType,
  }) => {
    const styleContainer = 'Pet-item-card-image-container';

    if (petImage) {
      return (
        <img
          alt=""
          className={`${styleContainer} Docs-pet-assigned-image ${className
            || ''}`}
          src={petImage}
        />
      );
    }

    return (
      <PetItemCardImage
        containerClassName={styleContainer}
        hideAddPhoto
        imageClassName="Docs-pet-assigned-image"
        initialsClassName="Docs-pet-assigned-name-initials"
        petImage={petImage}
        petName={petName}
        petType={petType}
      />
    );
  };

  const renderAssignedPets = (pets) => {
    if (isFolder || isEducationMaterialFile(itemRow.Id)) {
      return null;
    }

    if (!pets || !pets.length) {
      return <AssignPetsButton />;
    }

    const MAX_PET_ASSIGNED_VIEW_COUNT = 5;
    const maxPetAssignedReached = pets.length > MAX_PET_ASSIGNED_VIEW_COUNT;

    let petImages = pets
      .map((petId) => store.policies.allPets
        .find((pet) => pet.PetId === petId))
      .filter((pet) => !!pet);

    if (maxPetAssignedReached) {
      petImages = petImages.slice(0, MAX_PET_ASSIGNED_VIEW_COUNT);

      petImages.push({
        ClassName: 'Docs-pet-assigned-plus',
        ImageUrl: plusIcon,
        PetId: 0,
      });
    }

    return petImages.map((pet) => (
      <PetImage
        key={String(pet.PetId)}
        className={pet.ClassName}
        petImage={pet.ImageUrl}
        petName={pet.Name}
        petType={pet.PetType}
      />
    ));
  };

  const rowStyle = useMemo(() => {
    const docsTableItemRowCSS = isSelected
      ? 'Docs-table-item-row-selected' : 'Docs-table-item-row';

    return docsTableItemRowCSS;
  }, [isSelected]);

  const checkboxes = useMemo(() => ({
    selected: chboxSelected,
    unselected: chboxUnselected,
  }), []);

  return (
    <tr className={rowStyle} id={rowId}>
      <td>
        <TouchableDiv
          className="Docs-select-icon-checkbox-container"
          id="checkboxIconRowSelection"
          onClick={handleRowSelection}
        >
          <img
            alt=""
            src={isSelected ? checkboxes.selected : checkboxes.unselected}
          />
        </TouchableDiv>
      </td>

      <td>
        {renderIconAndName()}
      </td>

      <td>
        {formatDate(itemRow.Metadata.LastAddedDate || itemRow.LastUpdate)}
      </td>

      <td>
        {formatFileSize(itemRow.Metadata.Size)}
      </td>

      <td className="Row">
        {renderAssignedPets(itemRow.PetsId)}
      </td>

      {!isTrashFolder ? (
        <td>
          <InboxAndDocsMenu
            displayingFolderContent
            filePermissions={store.inboxAndDocs.filePermissions}
            folderPermissions={itemRow.FolderPermissions}
            goToFolder={changeLocation}
            isFolder={isFolder}
            itemId={itemRow.Id}
            itemInfo={itemRow}
            onEditTagPetClick={handleEditPetTagClick}
            parentFolderId={itemRow.ParentFolderId}
          />
        </td>
      ) : null}
    </tr>
  );
};

export { InboxAndDocsItemRow };
